<script lang="ts">
    import { gettext as _ } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch, type SimpleFetchResult } from 'hydria/services/session'
    import { getContext, onDestroy, onMount, tick } from 'svelte'
    import { fade } from 'svelte/transition'
    import { lg } from 'hydria/services/language'
    import { writable, type Writable } from 'svelte/store'
    import Icon from '@iconify/svelte'

    interface Props {
        tokenExpired: () => void
        saved: () => void
        close: () => void
    }

    let { tokenExpired, saved, close }: Props = $props()

    let stoken = getContext('securityToken') as Writable<string>

    // const MISMATCH_TIMEOUT_MS = 3000
    const CLOSE_TIMEOUT_MS = 7000

    let intlCode: string = '+1'
    let localPhone: string = $state('')
    let phone: string

    function prepPhone(intlCode: string, localPhone: string) {
        if (localPhone.length >= 10) {
            const clean = localPhone.replace(/[^0-9]/, '')
            if (clean.match(/^[0-9]{10}$/)) phone = `${intlCode}.${clean}`
        }
        sent = false
    }
    $effect(() => {
        prepPhone(intlCode, localPhone)
    })

    let sending: boolean = $state(false)
    let sent: boolean = $state(false)

    let verified: boolean | 'testing' = $state(false)
    let mismatched: boolean = $state(false)
    let code: string = $state('')
    let comment: string = ''
    let tested: Writable<Set<string>> = writable(new Set())

    // true: already an active SMS number, no point it adding it again
    let exists = $state(false)
    let token: string | undefined = undefined

    let saving: boolean = $state(false)
    let isSaved: boolean = $state(false)

    onMount(() => {
        focus('localPhone')
    })
    async function sendCode() {
        sending = true
        exists = false
        let r: SimpleFetchResult
        try {
            r = await simpleFetch(
                'POST',
                '/json/security/1/sms/send',
                {
                    lg: $lg,
                    phone: phone,
                    token: token ? token : null,
                },
                { headers: { 'x-security-token': $stoken } },
            )
        } catch (e: any) {
            r = e
        }
        if (r && r.data && r.data.ecode === 'SEC-TOKEN') {
            tokenExpired()
            return
        }

        sending = false

        if (!r || !r.data || !r.data.success) {
            notify({
                type: 'danger',
                title: $_('Authentification à deux facteurs'),
                text: 'Le code de véfication n’a pas pu être envoyé' + (r && r.data && r.data.error ? ` : ${r.data.error}` : '.'),
            })
            close()
            return
        }

        if (r.data.exists) {
            exists = true
            focus('localPhone', true)
        } else {
            focus('code')
        }
        code = ''
        tested.update((tested) => {
            tested.clear()
            return tested
        })

        token = r.data.token
        sent = true
    }

    async function validateCode() {
        if (verified !== false) return

        verified = 'testing'
        let r: SimpleFetchResult
        try {
            r = await simpleFetch('POST', '/json/security/1/sms/test', { lg: $lg, token: token, code: code }, { headers: { 'x-security-token': $stoken } })
        } catch (e: any) {
            r = e
        }
        if (r && r.data && r.data.ecode === 'SEC-TOKEN') {
            tokenExpired()
            return
        }

        if (!r || !r.data || !r.data.success) {
            verified = false
            return
        }

        if (r.data.matched) {
            verified = true
            mismatched = false
        } else {
            verified = false
            mismatched = true

            tested.update((tested) => {
                tested.add(code)
                return tested
            })

            focus('code', true)
        }
    }

    async function save() {
        if (verified !== true || saving) return

        saving = true
        let r: SimpleFetchResult
        try {
            r = await simpleFetch('PUT', '/json/security/1/sms', { lg: $lg, token: token, comment: comment }, { headers: { 'x-security-token': $stoken } })
        } catch (e: any) {
            r = e
        }
        if (r && r.data && r.data.ecode === 'SEC-TOKEN') {
            tokenExpired()
            return
        }

        if (!r || !r.data || !r.data.success) {
            saving = false
            return
        }

        if (!r.data.saved) {
            saving = false
            return
        }

        notify({
            type: 'success',
            title: $_('Authentification à deux facteurs'),
            text: $_('Le numéro de téléphone pour les messages texte a été ajouté.'),
        })
        saved()
        isSaved = true
    }

    $effect(() => {
        if (verified !== true && sent && !$tested.has(code) && code.match(/^[0-9]{6}$/)) validateCode()
    })

    let closeTimer: ReturnType<typeof setTimeout> | null = $state(null)
    $effect(() => {
        if (isSaved) {
            if (closeTimer) clearTimeout(closeTimer)
            closeTimer = setTimeout(() => {
                closeTimer = null
                close()
            }, CLOSE_TIMEOUT_MS)
        }
    })

    let localPhoneElement: HTMLInputElement | undefined = $state()
    let codeElement: HTMLInputElement | undefined = $state()

    function focus(what: 'localPhone' | 'code', select = false) {
        tick().then(() => {
            let el: HTMLInputElement | undefined
            if (what == 'localPhone') el = localPhoneElement
            else el = codeElement
            if (el) {
                el.focus()
                if (select) el.select()
            }
        })
    }

    let addedModalOpen = false
    if (!document.documentElement.classList.contains('modal-open')) {
        document.documentElement.classList.add('modal-open')
        addedModalOpen = true
    }

    onDestroy(() => {
        if (closeTimer) clearTimeout(closeTimer)
        if (addedModalOpen && document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.remove('modal-open')
        }
    })
</script>

<svelte:window
    onkeydown={(e) => {
        if (e.key === 'Escape') {
            if (!saving) {
                close()
            }
        }
    }}
/>

<div class="modal" role="dialog" transition:fade={{ duration: 250 }}>
    <div class="modal-dialog modal-lg">
        <div class="modal-content">
            <div class="modal-header text-bg-primary"><div>{$_('Code par SMS (message texte sur cellulaire)')}</div></div>
            {#if isSaved}
                <div class="modal-body">
                    <p>{$_('Le numéro de téléphone pour les messages texte a été ajouté.')}</p>
                </div>
            {:else}
                <div class="modal-body">
                    <p><strong>{$_('Pays ou code régionnal')}</strong></p>
                    <p class="text-muted">
                        {$_('Pour le moment, seul le Canada est supporté (+1).')}
                    </p>
                    <p><strong>{$_('Numéro de téléphone')}</strong></p>
                    <form
                        onsubmit={(e) => {
                            e.preventDefault()
                            sendCode()
                        }}
                    >
                        {#if exists}
                            <p class="text-danger">{$_('Ce numéro est déjà inscrit sur votre compte.')}</p>
                        {/if}
                        <div class="input-group mb-3">
                            <input type="text" bind:this={localPhoneElement} class="form-control" bind:value={localPhone} disabled={verified !== false} />
                            <button type="submit" class="btn btn-outline-primary btn-sm" disabled={sending || verified === true}>
                                {#if sending}
                                    <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                                {/if}
                                {$_('Texter un code à ce numéro')}
                            </button>
                        </div>
                    </form>

                    {#if sent}
                        <form
                            onsubmit={(e) => {
                                e.preventDefault()
                                validateCode()
                            }}
                        >
                            <p><strong>{$_('Validation')}</strong></p>
                            <p class="text-muted">{$_('Veuillez entrer le code reçu.')}</p>
                            <div class="input-group mb-3">
                                <input bind:this={codeElement} type="text" class="form-control" bind:value={code} disabled={verified !== false} />
                                <button type="submit" class="btn btn-sm" class:btn-outline-success={verified === true} class:btn-outline-primary={verified !== true} disabled={verified !== false || code.length != 6 || $tested.has(code)}>
                                    {#if verified === 'testing'}
                                        <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                                    {:else if $tested.has(code)}
                                        <Icon inline icon="mdi:times" />
                                    {:else if verified === true}
                                        <Icon inline icon="mdi:check" />
                                    {/if}
                                    {$_('Valider')}
                                </button>
                            </div>
                            {#if mismatched}
                                <p class="text-danger">{$_('Le code ne valide pas. Veuillez réessayer.')}</p>
                            {/if}
                        </form>
                    {/if}

                    <!-- {#if verified}
                        <p>{$_x('Le numéro a été vérifié. Vous pouvez maintenant l’ajouter.')}</p>
                        <p><strong>{$_x('Description')}</strong></p>
                        <p class="text-muted">{$_x('Vous pouvez ajouter une description à ce numéro.')}</p>
                        <p><input type="text" class="form-control" bind:value={comment} disabled={saving} /></p>
                    {/if} -->
                </div>
                <div class="modal-footer text-bg-light">
                    <button
                        type="button"
                        class="btn btn-outline-secondary"
                        disabled={saving}
                        onclick={() => {
                            close()
                        }}>{$_('Cancel')}</button
                    >
                    <button
                        type="button"
                        class="btn btn-primary"
                        onclick={() => {
                            save()
                        }}
                        disabled={verified !== true || saving}
                    >
                        {#if saving}<div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                            {$_('Saving…')}
                        {:else}{$_('Save')}
                        {/if}
                    </button>
                </div>
            {/if}
        </div>
    </div>
    <div
        class="modal-backdrop"
        onclick={() => {
            if (!saving) close()
        }}
        aria-hidden="true"
    ></div>
</div>
